// store/features/django/drug-response/drugResponseSlice.ts
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { getApiErrorMessage } from 'src/types/api';

export interface DrugItem {
  drug_name: string;
  pubchem?: string;
  etc?: string;
  synonyms?: string;
  pathway_name?: string;
  targets?: string;
  smiles?: string;
  drug_name_lower?: string;
}

interface DrugResponse {
  drug_list: DrugItem[];
}

export type DrugSortField = 'drug_name' | 'score' | 'pathway_name' | 'targets' | 'B_corr_clipped' | 'A_nrmse';
export type SortDir = 'asc' | 'desc';

const DRUG_PAGE_SIZE = 20;

export interface iDrugResponseState {
  // 원본 데이터
  response: DrugResponse | null;
  loading: boolean;
  error: string | null;

  // 필터 입력값 (input과 실제 적용값 분리)
  drugNameInput: string;
  drugName: string;
  drugPathwayInput: string;
  drugPathway: string;
  drugTargetsInput: string;
  drugTargets: string;

  // 정렬 & 페이지네이션
  drugSortBy: DrugSortField;
  drugSortDir: SortDir;
  drugPage: number;
  // Cutoff (필요시)
  drugScoreCutoff: number;
}

const initialState: iDrugResponseState = {
  response: null,
  loading: false,
  error: null,

  drugNameInput: '',
  drugName: '',
  drugPathwayInput: '',
  drugPathway: '',
  drugTargetsInput: '',
  drugTargets: '',
  drugSortBy: 'score',
  drugSortDir: 'desc',
  drugPage: 0,

  drugScoreCutoff: 0.5,
};

// CSV 라인 파싱 함수 (따옴표 안의 쉼표 처리)
const parseLine = (line: string): string[] => {
  const result: string[] = [];
  let current = '';
  let insideQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];

    if (char === '"') {
      insideQuotes = !insideQuotes;
    } else if (char === ',' && !insideQuotes) {
      result.push(current.trim());
      current = '';
    } else {
      current += char;
    }
  }
  result.push(current.trim());

  return result;
};

// CSV를 객체 배열로 파싱
const parseCSVToObjects = (text: string): DrugItem[] => {
  const lines = text.split('\n').filter((line) => line.trim());

  if (lines.length === 0) {
    console.warn('⚠️ CSV 파일이 비어있습니다.');
    return [];
  }

  // 헤더 파싱
  const headers = parseLine(lines[0]);

  // 각 컬럼의 인덱스 찾기
  const getColumnIndex = (columnName: string) => headers.indexOf(columnName);

  const indices = {
    drugName: getColumnIndex('drug_name'),
    pubchem: getColumnIndex('pubchem'),
    etc: getColumnIndex('etc'),
    synonyms: getColumnIndex('synonyms'),
    pathwayName: getColumnIndex('pathway_name'),
    targets: getColumnIndex('targets'),
    smiles: getColumnIndex('smiles'),
    drugNameLower: getColumnIndex('drug_name_lower'),
  };

  // 필수 컬럼 체크
  if (indices.drugName === -1) {
    console.error('❌ "drug_name" 컬럼을 찾을 수 없습니다!');
    return [];
  }

  // 데이터 파싱
  const dataList = lines
    .slice(1) // 헤더 제거
    .map((line, index) => {
      const values = parseLine(line);

      const item: DrugItem = {
        drug_name: values[indices.drugName] || '',
        pubchem: indices.pubchem !== -1 ? values[indices.pubchem] : undefined,
        etc: indices.etc !== -1 ? values[indices.etc] : undefined,
        synonyms: indices.synonyms !== -1 ? values[indices.synonyms] : undefined,
        pathway_name: indices.pathwayName !== -1 ? values[indices.pathwayName] : undefined,
        targets: indices.targets !== -1 ? values[indices.targets] : undefined,
        smiles: indices.smiles !== -1 ? values[indices.smiles] : undefined,
        drug_name_lower: indices.drugNameLower !== -1 ? values[indices.drugNameLower] : undefined,
      };

      return item;
    })
    .filter((item) => item.drug_name && item.drug_name !== '-'); // 유효한 데이터만

  return dataList;
};

// CSV에서 Drug 리스트 가져오기
export const getDrugResponseDataAPI = createAsyncThunk<DrugResponse>(
  'drugResponse/getFromCSV',
  async (_, { rejectWithValue }) => {
    try {
      const response = await fetch('/data/drug_all.csv');

      if (!response.ok) {
        throw new Error(`CSV 파일 로드 실패: ${response.status} ${response.statusText}`);
      }

      const text = await response.text();

      const drugList = parseCSVToObjects(text);

      if (drugList.length === 0) {
        throw new Error('파싱된 데이터가 없습니다.');
      }

      return { drug_list: drugList };
    } catch (error) {
      console.error('Drug List CSV 로딩 실패:', error);
      return rejectWithValue(getApiErrorMessage(error, 'Failed to load drug list'));
    }
  }
);

const drugResponseSlice = createSlice({
  name: 'drugResponse',
  initialState,
  reducers: {
    clearDrugResponseList: (state) => {
      state.response = null;
      state.error = null;
    },
    // Input 상태만 업데이트
    setDrugNameInput(state, action: PayloadAction<string>) {
      state.drugNameInput = action.payload;
    },
    setDrugPathwayInput(state, action: PayloadAction<string>) {
      state.drugPathwayInput = action.payload;
    },
    setDrugTargetsInput(state, action: PayloadAction<string>) {
      state.drugTargetsInput = action.payload;
    },

    // 필터 적용 (input -> actual filter)
    applyDrugNameFilter(state) {
      state.drugName = state.drugNameInput.trim();
      state.drugPage = 0;
    },
    applyDrugPathwayFilter(state) {
      state.drugPathway = state.drugPathwayInput.trim();
      state.drugPage = 0;
    },
    applyDrugTargetsFilter(state) {
      state.drugTargets = state.drugTargetsInput.trim();
      state.drugPage = 0;
    },

    // 통합 필터 적용
    applyAllFilters(state) {
      state.drugName = state.drugNameInput.trim();
      state.drugPathway = state.drugPathwayInput.trim();
      state.drugTargets = state.drugTargetsInput.trim();
      state.drugPage = 0;
    },

    // 개별 필터 리셋
    resetDrugNameFilter(state) {
      state.drugNameInput = '';
      state.drugName = '';
      state.drugPage = 0;
    },
    resetDrugPathwayFilter(state) {
      state.drugPathwayInput = '';
      state.drugPathway = '';
      state.drugPage = 0;
    },
    resetDrugTargetsFilter(state) {
      state.drugTargetsInput = '';
      state.drugTargets = '';
      state.drugPage = 0;
    },

    // 전체 리셋
    resetAllDrugFilters(state) {
      state.drugNameInput = '';
      state.drugName = '';
      state.drugPathwayInput = '';
      state.drugPathway = '';
      state.drugTargetsInput = '';
      state.drugTargets = '';
      state.drugPage = 0;
    },

    // 정렬
    setDrugSort(state, action: PayloadAction<DrugSortField>) {
      const field = action.payload;
      if (state.drugSortBy === field) {
        state.drugSortDir = state.drugSortDir === 'asc' ? 'desc' : 'asc';
      } else {
        state.drugSortBy = field;
        state.drugSortDir = 'asc';
      }
      state.drugPage = 0;
    },

    // 페이지네이션
    setDrugPage(state, action: PayloadAction<number>) {
      state.drugPage = action.payload;
    },

    // Cutoff
    setDrugScoreCutoff(state, action: PayloadAction<number>) {
      state.drugScoreCutoff = Math.min(1, Math.max(0, action.payload));
    },

    clearError(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getDrugResponseDataAPI.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getDrugResponseDataAPI.fulfilled, (state, action: PayloadAction<DrugResponse>) => {
        state.loading = false;
        state.response = action.payload;
      })
      .addCase(getDrugResponseDataAPI.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) || 'Failed to load drug list';
        console.error('Redux 에러:', action.payload);
      });
  },
});

export const {
  clearDrugResponseList,
  setDrugNameInput,
  setDrugPathwayInput,
  setDrugTargetsInput,
  applyDrugNameFilter,
  applyDrugPathwayFilter,
  applyDrugTargetsFilter,
  applyAllFilters,
  resetDrugNameFilter,
  resetDrugPathwayFilter,
  resetDrugTargetsFilter,
  resetAllDrugFilters,
  setDrugSort,
  setDrugPage,
  setDrugScoreCutoff,
  clearError,
} = drugResponseSlice.actions;

export { DRUG_PAGE_SIZE };
export default drugResponseSlice.reducer;